import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../services/api';
import { toast } from '../components/common/Toast';
import { useAuth } from '../context/AuthContext';
import { errMsg } from '../utils/helpers';
import {
  Settings as SettingsIcon, Save, Bell, Mail, Send, AlertTriangle,
  CheckCircle2, Plus, X, Clock, CalendarClock, Lock,
} from 'lucide-react';

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());

const pad2 = (n) => String(n).padStart(2, '0');

// A short list for the runtimes that cannot enumerate zones, and as the head of
// the list everywhere: these are the ones this deployment actually uses.
const COMMON_ZONES = [
  'America/Bogota', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Mexico_City', 'Europe/Madrid', 'UTC',
];

function allTimezones() {
  try {
    const all = Intl.supportedValuesOf('timeZone');
    if (all?.length) return [...COMMON_ZONES, ...all.filter((z) => !COMMON_ZONES.includes(z))];
  } catch { /* older browser — fall back to the short list */ }
  return COMMON_ZONES;
}

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [form, setForm]       = useState(null);
  const [meta, setMeta]       = useState({ emailConfigured: false, lastReminderRunAt: null,
                                           nextReminderRunAt: null, maxDayOfMonth: 28, scheduleLocked: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    settingsAPI.get()
      .then(r => {
        const d = r.data.data;
        setForm({
          rentReminderEnabled:     d.rentReminderEnabled,
          rentReminderDaysBefore:  d.rentReminderDaysBefore,
          arrearsLookbackMonths:   d.arrearsLookbackMonths,
          rentReminderEmail:       d.rentReminderEmail,
          rentReminderExtraEmails: d.rentReminderExtraEmails || [],
          notificationLanguage:    d.notificationLanguage || 'en',
          reminderFrequency:       d.reminderFrequency || 'daily',
          reminderHour:            d.reminderHour ?? 8,
          reminderMinute:          d.reminderMinute ?? 0,
          reminderWeekday:         d.reminderWeekday ?? 1,
          reminderDayOfMonth:      d.reminderDayOfMonth ?? 1,
          reminderTimezone:        d.reminderTimezone || 'America/New_York',
        });
        setMeta({
          emailConfigured: d.emailConfigured,
          lastReminderRunAt: d.lastReminderRunAt,
          nextReminderRunAt: d.nextReminderRunAt,
          cronExpression: d.cronExpression,
          maxDayOfMonth: d.maxDayOfMonth ?? 28,
          scheduleLocked: d.scheduleLockedByServer,
        });
      })
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Weekday names come from the browser in the language the app is in, rather
  // than from seven more translation keys that would only ever say this.
  const weekdays = useMemo(() => {
    // Formatted in UTC on purpose: without it the formatter reads the dates below
    // in the browser's own zone, and west of Greenwich every label slips a day.
    const f = new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' });
    // 2024-01-07 was a Sunday, so index 0 is Sunday — the same as cron.
    return Array.from({ length: 7 }, (_, i) => {
      const name = f.format(new Date(Date.UTC(2024, 0, 7 + i)));
      return name.charAt(0).toUpperCase() + name.slice(1);
    });
  }, [i18n.language]);

  const zones = useMemo(allTimezones, []);
  const scheduleLocked = meta.scheduleLocked || !isAdmin;

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!isEmail(e))                            return toast.error(t('invalidEmail'));
    if (form.rentReminderExtraEmails.includes(e)) return toast.error(t('emailAlreadyAdded'));
    set('rentReminderExtraEmails', [...form.rentReminderExtraEmails, e]);
    setNewEmail('');
  };

  const save = async () => {
    if (!Number.isInteger(Number(form.rentReminderDaysBefore)) ||
        form.rentReminderDaysBefore < 1 || form.rentReminderDaysBefore > 90) {
      return toast.error(t('reminderDaysRange'));
    }
    if (!Number.isInteger(Number(form.arrearsLookbackMonths)) ||
        form.arrearsLookbackMonths < 1 || form.arrearsLookbackMonths > 60) {
      return toast.error(t('arrearsLookbackRange'));
    }
    setSaving(true);
    try {
      const r = await settingsAPI.update(form);
      toast.success(t('settingsSaved'));
      setMeta(m => ({ ...m,
        nextReminderRunAt: r.data.nextReminderRunAt,
        cronExpression: r.data.cronExpression,
      }));
      if (r.data.rescheduled === false) toast.error(t('rescheduleFailed'));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setTesting(true);
    try {
      const r = await settingsAPI.runRentReminders(true);
      const d = r.data.data;
      if (d.skipped) {
        toast.info(d.reason === 'nothing-due' ? t('noUpcomingRent') : t('remindersSkipped'));
      } else {
        toast.success(t('remindersSentSummary', { count: d.notified, recipients: d.recipients }));
        if (!d.email?.sent) toast.error(t('emailNotSent') + ': ' + (d.email?.reason || '—'));
      }
      setMeta(m => ({ ...m, lastReminderRunAt: new Date().toISOString() }));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTesting(false); }
  };

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner"/></div>;
  if (!form)   return null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('settings')}</h1>
          <p className="page-subtitle">{t('settingsSubtitle')}</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <Save size={15}/>{saving ? t('saving') : t('saveChanges')}
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="card" style={{ padding:14, marginBottom:16, display:'flex', gap:10, alignItems:'center',
          background:'var(--brand-ghost)', borderColor:'var(--brand)' }}>
          <AlertTriangle size={16} color="var(--brand)"/>
          <span style={{ fontSize:13 }}>{t('settingsReadOnly')}</span>
        </div>
      )}

      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header">
          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
            <Bell size={17} color="var(--brand)"/>
            <div>
              <div style={{ fontWeight:700, fontSize:15 }}>{t('rentReminders')}</div>
              <div style={{ fontSize:12, color:'var(--ink-40)', marginTop:1 }}>{t('rentRemindersHelp')}</div>
            </div>
          </div>
        </div>

        <div style={{ padding:'18px 20px', display:'flex', flexDirection:'column', gap:18 }}>

          {/* Enable */}
          <Row label={t('enableRentReminders')} help={t('enableRentRemindersHelp')}>
            <Toggle checked={form.rentReminderEnabled} disabled={!isAdmin}
              onChange={v => set('rentReminderEnabled', v)}/>
          </Row>

          {meta.scheduleLocked && (
            <div style={{ display:'flex', gap:9, alignItems:'flex-start', padding:'11px 13px', borderRadius:8,
              background:'rgba(245,158,11,.09)', border:'1px solid rgba(245,158,11,.3)' }}>
              <Lock size={15} color="var(--amber)" style={{ flexShrink:0, marginTop:1 }}/>
              <div style={{ fontSize:12.5, lineHeight:1.5 }}>
                <div style={{ fontWeight:600 }}>{t('scheduleLocked')}</div>
                <div style={{ color:'var(--ink-40)' }}>{t('scheduleLockedHelp')}</div>
              </div>
            </div>
          )}

          {/* How often the sweep runs */}
          <Row label={t('reminderFrequency')} help={t('reminderFrequencyHelp')}>
            <select className="form-input" disabled={scheduleLocked} style={{ width:170 }}
              value={form.reminderFrequency}
              onChange={e => set('reminderFrequency', e.target.value)}>
              <option value="daily">{t('freqDaily')}</option>
              <option value="weekly">{t('freqWeekly')}</option>
              <option value="monthly">{t('freqMonthly')}</option>
            </select>
          </Row>

          {form.reminderFrequency === 'weekly' && (
            <Row label={t('reminderWeekdayLabel')} help={t('reminderWeekdayHelp')}>
              <select className="form-input" disabled={scheduleLocked} style={{ width:170 }}
                value={form.reminderWeekday}
                onChange={e => set('reminderWeekday', parseInt(e.target.value, 10))}>
                {weekdays.map((name, i) => <option key={i} value={i}>{name}</option>)}
              </select>
            </Row>
          )}

          {form.reminderFrequency === 'monthly' && (
            <Row label={t('reminderDayOfMonthLabel')} help={t('reminderDayOfMonthHelp', { max: meta.maxDayOfMonth })}>
              <input type="number" min={1} max={meta.maxDayOfMonth} className="form-input" disabled={scheduleLocked}
                value={form.reminderDayOfMonth}
                onChange={e => set('reminderDayOfMonth', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                style={{ width:90, textAlign:'center' }}/>
            </Row>
          )}

          <Row label={t('reminderTime')} help={t('reminderTimeHelp')}>
            <input type="time" className="form-input" disabled={scheduleLocked} style={{ width:130 }}
              value={pad2(form.reminderHour) + ':' + pad2(form.reminderMinute)}
              onChange={e => {
                const [h, m] = e.target.value.split(':');
                if (h === undefined || m === undefined) return;
                setForm(f => ({ ...f, reminderHour: parseInt(h, 10), reminderMinute: parseInt(m, 10) }));
              }}/>
          </Row>

          <Row label={t('reminderTimezoneLabel')} help={t('reminderTimezoneHelp')}>
            <select className="form-input" disabled={scheduleLocked} style={{ width:230 }}
              value={form.reminderTimezone}
              onChange={e => set('reminderTimezone', e.target.value)}>
              {!zones.includes(form.reminderTimezone) && (
                <option value={form.reminderTimezone}>{form.reminderTimezone}</option>
              )}
              {zones.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </Row>

          {/* Days before */}
          <Row label={t('daysBeforeDue')} help={t('daysBeforeDueHelp')}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="number" min={1} max={90} className="form-input" disabled={!isAdmin}
                value={form.rentReminderDaysBefore}
                onChange={e => set('rentReminderDaysBefore', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                style={{ width:90, textAlign:'center' }}/>
              <span style={{ fontSize:13, color:'var(--ink-40)' }}>{t('daysBeforeDueUnit')}</span>
            </div>
          </Row>

          {/* Arrears lookback */}
          <Row label={t('arrearsLookback')} help={t('arrearsLookbackHelp')}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="number" min={1} max={60} className="form-input" disabled={!isAdmin}
                value={form.arrearsLookbackMonths}
                onChange={e => set('arrearsLookbackMonths', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                style={{ width:90, textAlign:'center' }}/>
              <span style={{ fontSize:13, color:'var(--ink-40)' }}>{t('arrearsLookbackUnit')}</span>
            </div>
          </Row>

          {/* Fallback language for anyone without a choice of their own */}
          <Row label={t('notificationLanguage')} help={t('notificationLanguageHelp')}>
            <select className="form-input" disabled={!isAdmin} style={{ width:150 }}
              value={form.notificationLanguage}
              onChange={e => set('notificationLanguage', e.target.value)}>
              <option value="en">{t('english')}</option>
              <option value="es">{t('spanish')}</option>
            </select>
          </Row>

          {/* Email toggle */}
          <Row label={t('sendEmailDigest')} help={t('sendEmailDigestHelp')}>
            <Toggle checked={form.rentReminderEmail} disabled={!isAdmin}
              onChange={v => set('rentReminderEmail', v)}/>
          </Row>

          {/* SES status */}
          <div style={{ display:'flex', gap:9, alignItems:'flex-start', padding:'11px 13px', borderRadius:8,
            background: meta.emailConfigured ? 'rgba(5,150,105,.07)' : 'rgba(245,158,11,.09)',
            border: '1px solid ' + (meta.emailConfigured ? 'rgba(5,150,105,.25)' : 'rgba(245,158,11,.3)') }}>
            {meta.emailConfigured
              ? <CheckCircle2 size={15} color="var(--emerald)" style={{ flexShrink:0, marginTop:1 }}/>
              : <AlertTriangle size={15} color="var(--amber)" style={{ flexShrink:0, marginTop:1 }}/>}
            <div style={{ fontSize:12.5, lineHeight:1.5 }}>
              <div style={{ fontWeight:600 }}>
                {meta.emailConfigured ? t('emailConfigured') : t('emailNotConfigured')}
              </div>
              <div style={{ color:'var(--ink-40)' }}>
                {meta.emailConfigured ? t('emailConfiguredHelp') : t('emailNotConfiguredHelp')}
              </div>
            </div>
          </div>

          {/* Extra recipients */}
          <div>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:3 }}>{t('extraRecipients')}</div>
            <div style={{ fontSize:12, color:'var(--ink-40)', marginBottom:9 }}>{t('extraRecipientsHelp')}</div>

            <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginBottom:9 }}>
              {form.rentReminderExtraEmails.map(e => (
                <span key={e} className="badge badge-brand" style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'5px 9px' }}>
                  <Mail size={11}/>{e}
                  {isAdmin && (
                    <button onClick={() => set('rentReminderExtraEmails', form.rentReminderExtraEmails.filter(x => x !== e))}
                      style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', color:'inherit' }}
                      aria-label={t('remove')}>
                      <X size={12}/>
                    </button>
                  )}
                </span>
              ))}
              {!form.rentReminderExtraEmails.length && (
                <span style={{ fontSize:12.5, color:'var(--ink-20)' }}>{t('noExtraRecipients')}</span>
              )}
            </div>

            {isAdmin && (
              <div style={{ display:'flex', gap:8, maxWidth:420 }}>
                <input className="form-input" type="email" placeholder="name@example.com"
                  value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}/>
                <button className="btn btn-secondary" onClick={addEmail}><Plus size={14}/>{t('add')}</button>
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div style={{ padding:'13px 20px', borderTop:'1px solid var(--border)', background:'var(--surface-2)',
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
            <div style={{ fontSize:12, color:'var(--ink-40)', display:'flex', flexWrap:'wrap', gap:'4px 14px' }}>
              <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                <Clock size={13}/>
                {meta.lastReminderRunAt
                  ? t('lastRun', { when: new Date(meta.lastReminderRunAt).toLocaleString() })
                  : t('neverRun')}
              </span>
              {meta.nextReminderRunAt && (
                <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <CalendarClock size={13}/>
                  {t('nextRun', { when: new Date(meta.nextReminderRunAt).toLocaleString() })}
                </span>
              )}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={runNow} disabled={testing}>
              <Send size={13}/>{testing ? t('sending') : t('runRemindersNow')}
            </button>
          </div>
        )}
      </div>

      <div style={{ fontSize:12, color:'var(--ink-20)', display:'flex', alignItems:'center', gap:6 }}>
        <SettingsIcon size={12}/>{t('reminderScheduleNote')}
      </div>
    </div>
  );
}

function Row({ label, help, children }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
      <div style={{ minWidth:200, flex:1 }}>
        <div style={{ fontSize:13, fontWeight:600 }}>{label}</div>
        {help && <div style={{ fontSize:12, color:'var(--ink-40)', marginTop:2 }}>{help}</div>}
      </div>
      <div style={{ flexShrink:0 }}>{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width:44, height:25, borderRadius:999, border:'none', padding:3, cursor: disabled ? 'not-allowed' : 'pointer',
        background: checked ? 'var(--brand)' : 'var(--fog-dark)', opacity: disabled ? .55 : 1,
        display:'flex', justifyContent: checked ? 'flex-end' : 'flex-start', alignItems:'center',
        transition:'background 160ms ease',
      }}>
      <span style={{ width:19, height:19, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.2)' }}/>
    </button>
  );
}
